import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
	HEADER_PAD_CLASS,
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	TEXT_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import type { RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { ErrorModal } from "@dexteel/mesf-core";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import {
	PRODUCT_MILL_SETUP_INITIAL_VALUES,
	type MillSetupGridRow,
	type ProductMillSetup,
	type StandOption,
} from "../models/ProductMillSetup";
import {
	useActiveStands,
	useProductMillSetup,
} from "../hooks/useProductMillSetup";
import {
	useDeleteProductMillSetup,
	useUpsertProductMillSetup,
} from "../hooks/useProductMillSetupMutation";
import {
	type MillSetupGridContext,
	useMillSetupTableData,
} from "../hooks/useMillSetupTableData";
import { ProductMillSetupFormModal } from "./ProductMillSetupFormModal";

const ROW_HEIGHT = 28;

interface ProductMillSetupTableProps {
	productSizeId: number;
	disabled?: boolean;
	/** The ADD button lives in the tab strip, so the parent opens the add form. */
	isAdding?: boolean;
	onAddDone?: () => void;
}

/**
 * Every stand of the line, in mill order: the stand with its main pass, then one
 * line per alternative pass, or an empty greyed line when the product has
 * nothing there.
 */
const buildRows = (
	stands: StandOption[],
	setups: ProductMillSetup[],
): MillSetupGridRow[] => {
	const rows: MillSetupGridRow[] = [];
	const setupByStand = new Map(setups.map((s) => [s.StandId, s]));
	const orderedStands = [...stands].sort(
		(a, b) => a.StandNumber - b.StandNumber,
	);
	const standIds = new Set(orderedStands.map((s) => s.Id));

	const pushSetup = (setup: ProductMillSetup) => {
		const altIds = (setup.AlternativePassIds ?? "")
			.split(",")
			.filter(Boolean)
			.map(Number);
		const altNames = (setup.AlternativePassNames ?? "").split(", ");
		rows.push({
			...setup,
			RowKey: `setup-${setup.Id}`,
			IsEmpty: false,
			IsAlternative: false,
			AlternativesCount: altIds.length,
		});
		altIds.forEach((passId, i) => {
			rows.push({
				...PRODUCT_MILL_SETUP_INITIAL_VALUES,
				Id: setup.Id,
				ProductSizeId: setup.ProductSizeId,
				StandId: setup.StandId,
				StandName: setup.StandName,
				StandNumber: setup.StandNumber,
				PassId: passId,
				PassIdName: altNames[i] ?? null,
				RowKey: `alt-${setup.Id}-${passId}`,
				IsEmpty: false,
				IsAlternative: true,
				AlternativesCount: altIds.length,
			});
		});
	};

	for (const stand of orderedStands) {
		const setup = setupByStand.get(stand.Id);
		if (setup) {
			pushSetup(setup);
			continue;
		}
		rows.push({
			...PRODUCT_MILL_SETUP_INITIAL_VALUES,
			StandId: stand.Id,
			StandName: stand.Name,
			StandNumber: stand.StandNumber,
			RowKey: `empty-${stand.Id}`,
			IsEmpty: true,
			IsAlternative: false,
			AlternativesCount: 0,
		});
	}

	for (const setup of setups) {
		if (setup.StandId == null || !standIds.has(setup.StandId)) pushSetup(setup);
	}

	return rows;
};

export const ProductMillSetupTable = ({
	productSizeId,
	isAdding = false,
	onAddDone,
}: ProductMillSetupTableProps) => {
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [editingRow, setEditingRow] = useState<MillSetupGridRow | null>(null);
	const [rowToDelete, setRowToDelete] = useState<MillSetupGridRow | null>(null);
	const [expandedStands, setExpandedStands] = useState<Set<number>>(
		() => new Set(),
	);

	const { millSetupRows, isLoading } = useProductMillSetup(productSizeId);
	const { data: stands = [], isLoading: isLoadingStands } = useActiveStands();

	const rows = useMemo(
		() => buildRows(stands, millSetupRows),
		[stands, millSetupRows],
	);
	const visibleRows = useMemo(
		() =>
			rows.filter(
				(r) =>
					!r.IsAlternative ||
					(r.StandId != null && expandedStands.has(r.StandId)),
			),
		[rows, expandedStands],
	);
	const configuredStandIds = useMemo(
		() => new Set(millSetupRows.map((s) => s.StandId as number)),
		[millSetupRows],
	);

	const closeForm = useCallback(() => {
		setFormModalOpen(false);
		setEditingRow(null);
		onAddDone?.();
	}, [onAddDone]);

	const { upsertMillSetup, isUpserting } = useUpsertProductMillSetup({
		onSuccess: () => {
			setNotification("Mill setup saved successfully");
			closeForm();
		},
		onError: (err) => setError(err.message),
	});

	const { deleteMillSetup, isDeleting } = useDeleteProductMillSetup({
		onSuccess: () => {
			setNotification("Mill setup deleted successfully");
			setRowToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleToggleExpandStand = useCallback((standId: number) => {
		setExpandedStands((prev) => {
			const next = new Set(prev);
			if (next.has(standId)) next.delete(standId);
			else next.add(standId);
			return next;
		});
	}, []);

	const context = useMemo<MillSetupGridContext>(
		() => ({ expandedStands, onToggleExpandStand: handleToggleExpandStand }),
		[expandedStands, handleToggleExpandStand],
	);

	const { columnDefs } = useMillSetupTableData({ onDelete: setRowToDelete });
	// One line per stand, so the whole mill fits the tab without scrolling.
	const defaultColDef = useMemo(
		() => ({
			...globalDefaultColDef,
			sortable: false,
			wrapText: false,
			autoHeight: false,
			cellClass: ONE_LINE_CELL_CLASS,
			headerClass: HEADER_PAD_CLASS,
			tooltipValueGetter: (params: { value?: unknown }) =>
				params.value == null ? "" : String(params.value),
		}),
		[],
	);

	const rowClassRules = useMemo(
		() => ({
			"mill-setup-empty": (params: { data?: MillSetupGridRow }) =>
				!!params.data?.IsEmpty,
			"mill-setup-alternative": (params: { data?: MillSetupGridRow }) =>
				!!params.data?.IsAlternative,
		}),
		[],
	);

	// Any line of a stand, alternatives included, opens that stand's form.
	const onRowClicked = useCallback(
		(event: RowClickedEvent<MillSetupGridRow>) => {
			const target = event.event?.target as HTMLElement;
			if (target?.closest(".MuiIconButton-root, .mill-setup-toggle")) return;
			if (!event.data) return;
			const standId = event.data.StandId;
			const main = event.data.IsAlternative
				? rows.find((r) => !r.IsAlternative && r.StandId === standId)
				: event.data;
			setEditingRow(main ?? event.data);
			setFormModalOpen(true);
		},
		[rows],
	);

	const getRowId = useCallback(
		(params: { data: MillSetupGridRow }) => params.data.RowKey,
		[],
	);

	const handleConfirmDelete = useCallback(() => {
		if (rowToDelete?.Id != null) deleteMillSetup(rowToDelete.Id);
	}, [rowToDelete, deleteMillSetup]);

	const deleteItemName = rowToDelete
		? `${rowToDelete.StandName ?? ""} ${[rowToDelete.PassIdName, rowToDelete.AlternativePassNames].filter(Boolean).join(", ")}`.trim()
		: "";

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				// A text node in a centred flex cell is clipped on both sides; as a block it gets the ellipsis.
				[`& .ag-cell.${ONE_LINE_CELL_CLASS}:not(.${TEXT_CELL_CLASS}):not(.${NUMBER_CELL_CLASS}):not([col-id="StandName"])`]:
					{
						display: "block !important",
						textAlign: "center",
						textOverflow: "ellipsis",
						lineHeight: `${ROW_HEIGHT - 1}px !important`,
					},
				"& .mill-setup-empty": { color: "#94a3b8" },
				"& .mill-setup-alternative": {
					backgroundColor: "#f3f4f6 !important",
					color: "#475569",
				},
			}}
		>
			<OneLineCellStyles />
			<AgGridReact<MillSetupGridRow>
				rowData={visibleRows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				context={context}
				rowClassRules={rowClassRules}
				tooltipShowDelay={400}
				onRowClicked={onRowClicked}
				getRowId={getRowId}
				suppressContextMenu
				suppressColumnVirtualisation
				suppressCellFocus
				animateRows={false}
				rowHeight={ROW_HEIGHT}
				headerHeight={28}
				enableBrowserTooltips
				loading={isLoading || isLoadingStands}
			/>

			<ProductMillSetupFormModal
				open={formModalOpen || isAdding}
				productSizeId={productSizeId}
				initialData={isAdding ? null : editingRow}
				configuredStandIds={configuredStandIds}
				onSave={upsertMillSetup}
				onCancel={closeForm}
				isSaving={isUpserting}
			/>

			<DeleteConfirmModal
				open={rowToDelete != null}
				title="DELETE MILL SETUP"
				itemName={deleteItemName}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={() => setRowToDelete(null)}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
